/*
* PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
* Copyright (c) 2019 Pearson Education, Inc.
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains the property of
* Pearson Education, Inc. The intellectual and technical concepts contained
* herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
* and Foreign Patents, patent applications, and are protected by trade secret
* or copyright law. Dissemination of this information, reproduction of this
* material, and copying or distribution of this software is strictly forbidden
* unless prior written permission is obtained from Pearson Education, Inc.
*/
import React from 'react'
import { IntlProvider, FormattedMessage } from 'react-intl'
import PropTypes from 'prop-types'

import aria from './data-grid'
import messages from '../messages'
import './reports-table.scss'

class SortableTable extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      data: this.appendIndex(this.props.items),
      sortBy: this.props.sortBy,
      sortType: this.props.sortType || 'string',
      isAsc: this.props.isAsc
    }
  }

  componentDidMount () {
    new aria.Grid(this.table) // eslint-disable-line no-new
  }

  componentDidUpdate (prevProps) {
    if (this.props.items !== prevProps.items) {
      let sortedData = this.appendIndex(this.props.items)
      switch (this.state.sortType) {
        case 'numeric':
          sortedData.sort(this.sortByNumber(this.state.sortBy, this.state.isAsc))
          break
        case 'string':
          sortedData.sort(this.sortByString(this.state.sortBy, this.state.isAsc))
          break
        case 'datetime':
          sortedData.sort(this.sortByDateTime(this.state.sortBy, this.state.isAsc))
          break
        default:
          break
      }

      this.setState({
        data: sortedData
      }, () => new aria.Grid(this.table))
    }
  }

  appendIndex = (items) => {
    return items.map((item, index) => {
      item['sortableIndex'] = index
      return item
    })
  }

  sortByNumber = (columnName, isAsc) => (a, b) => {
    let compare = a[columnName] - b[columnName]
    if (compare === 0) compare = a['sortableIndex'] - b['sortableIndex']
    if (!isAsc) compare = -compare
    return compare
  }

  sortByString = (columnName, isAsc) => (a, b) => {
    let compare = (a[columnName] || '').localeCompare(b[columnName] || '')
    if (compare === 0) compare = a['sortableIndex'] - b['sortableIndex']
    if (!isAsc) compare = -compare
    return compare
  }

  sortByDateTime = (columnName, isAsc) => (a, b) => {
    let compare = new Date(a[columnName]) - new Date(b[columnName])
    if (compare === 0) compare = a['sortableIndex'] - b['sortableIndex']
    if (!isAsc) compare = -compare
    return compare
  }

  onSortNumeric = (columnName) => (e) => {
    const order = (this.state.sortBy === columnName) ? !this.state.isAsc : true
    const sortedData = this.state.data.concat().sort(this.sortByNumber(columnName, order))

    this.setState({
      data: sortedData,
      sortBy: columnName,
      sortType: 'numeric',
      isAsc: order
    }, () => new aria.Grid(this.table))
  }

  onSortString = (columnName) => (e) => {
    const order = (this.state.sortBy === columnName) ? !this.state.isAsc : true
    const sortedData = this.state.data.concat().sort(this.sortByString(columnName, order))

    this.setState({
      data: sortedData,
      sortBy: columnName,
      sortType: 'string',
      isAsc: order
    }, () => new aria.Grid(this.table))
  }

  onSortDateTime = (columnName) => (e) => {
    const order = (this.state.sortBy === columnName) ? !this.state.isAsc : true
    const sortedData = this.state.data.concat().sort(this.sortByDateTime(columnName, order))

    this.setState({
      data: sortedData,
      sortBy: columnName,
      sortType: 'datetime',
      isAsc: order
    }, () => new aria.Grid(this.table))
  }

  render () {
    const children = React.Children.map(this.props.children, child => {
      let click = {}
      switch (child.props.sortType) {
        case 'numeric':
          click = {
            onClick: this.onSortNumeric(child.props.columnName),
            isSortedIndex: this.state === undefined
              ? this.props.sortBy === child.props.columnName
              : this.state.sortBy === child.props.columnName,
            isAscending: this.state.isAsc
          }
          break
        case 'string':
          click = {
            onClick: this.onSortString(child.props.columnName),
            isSortedIndex: this.state === undefined
              ? this.props.sortBy === child.props.columnName
              : this.state.sortBy === child.props.columnName,
            isAscending: this.state.isAsc
          }
          break
        case 'datetime':
          click = {
            onClick: this.onSortDateTime(child.props.columnName),
            isSortedIndex: this.state === undefined
              ? this.props.sortBy === child.props.columnName
              : this.state.sortBy === child.props.columnName,
            isAscending: this.state.isAsc
          }
          break
        default:
          click = {}
          break
      }
      return React.cloneElement(child, click)
    })

    const title = this.props.title && this.props.title !== ''
      ? <FormattedMessage
        id='messages.sortableTableCaption'
        description={this.props.title}
        defaultMessage={this.props.title}
        tagName='caption'
      />
      : null

    return (
      <IntlProvider locale={this.props.locale} messages={messages}>
        <table
          aria-label={this.props.description + ' sorted by ' + this.state.sortBy +
            ' in ' + (this.state.isAsc ? 'Ascending' : 'Descending') + ' Order'
          }
          className='Webcomponents General Collections SortableTable sortable'
          ref={e => {
            this.table = e
          }}
        >
          {title}
          <thead>
            <tr>
              {children}
            </tr>
          </thead>
          <tbody>
            {this.props.populateRows(this.state.data)}
          </tbody>
        </table>
      </IntlProvider>
    )
  }
}

SortableTable.propTypes = {
  items: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  populateRows: PropTypes.func.isRequired,
  sortBy: PropTypes.string,
  sortType: PropTypes.string,
  isAsc: PropTypes.bool,
  locale: PropTypes.string,
  children: PropTypes.array
}

SortableTable.defaultProps = {
  isAsc: true,
  locale: 'en'
}

export default SortableTable
